import { memo } from 'react';
import { formatDate, formatUserName } from '@/lib';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '@/components';
import { Card } from '@/components/ui/card';
import { ImageWithFallback } from '@/components';
import type { FriendItemAddedPost } from '../model';

interface FriendItemAddedCardProps {
  post: FriendItemAddedPost;
  onItemClick: (wishlistId: string, itemId: string) => void;
  onUserClick: (userId: string) => void;
}

/**
 * Карточка добавления желания другом
 */
export const FriendItemAddedCard = memo(function FriendItemAddedCard({
  post,
  onItemClick,
  onUserClick,
}: FriendItemAddedCardProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Card className="pt-4 pb-4 px-0 gap-0">
      {/* Заголовок с аватаром */}
      <div className="flex items-center gap-3.5 mb-4 px-4">
        <button
          type="button"
          className="flex-shrink-0"
          onClick={() => onUserClick(post.friend.id)}
          aria-label={post.friend.username || undefined}
        >
          <UserAvatar
            userId={post.friend.id}
            avatarUrl={post.friend.avatarUrl}
            displayName={post.friend.displayName}
            lastName={post.friend.lastName}
            username={post.friend.username}
            size="default"
          />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground">
            <button
              type="button"
              className="font-semibold"
              onClick={() => onUserClick(post.friend.id)}
            >
              {formatUserName(post.friend.displayName, post.friend.lastName, { short: true }) || post.friend.username}
            </button>{' '}
            <span className="text-muted-foreground">
              {t('feed:feed.friendItemAdded')}
            </span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {formatDate(post.createdAt)}
          </p>
        </div>
      </div>

      {/* Желание */}
      <button
        type="button"
        className="active:opacity-80 transition-opacity w-full text-left"
        onClick={() => onItemClick(post.item.wishlistId, post.item.id)}
      >
        {/* Изображение если есть */}
        {post.item.imageUrl && (
          <div className="overflow-hidden mb-4">
            <ImageWithFallback
              src={post.item.imageUrl}
              alt={post.item.title}
              loading="lazy"
              className="w-full aspect-square object-cover"
            />
          </div>
        )}

        {/* Название желания */}
        <p className="font-semibold text-sm text-foreground px-4">{post.item.title}</p>

        {/* Название вишлиста */}
        <p className="text-xs text-muted-foreground mt-0.5 px-4">
          {t('feed:feed.inWishlist')} «{post.wishlist.title}»
        </p>
      </button>
    </Card>
  );
});